<template>
  <div class="playlist-wrapper">
    <mu-divider v-if="!edit || simple" class="divider-padding"/>
    <div class="float-right" v-if="edit">
      <ButtonOrListGroup icon="add" :simple="simple">
        <template slot="list">
          <ManagePlaylist 
            :editing="addingPlaylist" 
            :title="$t('button.add') + $t('space') + $t('playlist.title')"
            @cancel="onCancel"
            @save="onAddPlaylist"
            create
            >
            <ButtonOrListItem 
              :simple="simple"
              small 
              icon="playlist_add" 
              color="primary" 
              @click="addingPlaylist = true" 
              :text="$t('button.add') + $t('space') + $t('playlist.title')"
              />
          </ManagePlaylist>
          <ManageMedia
            :editing="addingMedia" 
            :title="$t('button.add') + $t('space') + $t('media.title')"
            @cancel="onCancel"
            @save="onAddMedia"
            create
            >
            <ButtonOrListItem 
              :simple="simple"
              small 
              icon="library_add" 
              color="primary" 
              @click="addingMedia = true" 
              :text="$t('button.add') + $t('space') + $t('media.title')"
              />
          </ManageMedia>
        </template>
      </ButtonOrListGroup>
      <ManagePlaylist 
        v-model="data.obj" 
        :deleting="deleting" 
        :editing="editing" 
        :title="$t('button.edit') + $t('space') + $t('playlist.title')"
        @cancel="onCancel"
        @save="onSave"
        @delete="onDelete"
        class="float-right"
        >
        <ButtonOrListGroup :simple="simple">
          <template slot="list">
            <ButtonOrListItem 
              :simple="simple"
              small 
              icon="edit" 
              color="primary" 
              @click="editing = true" 
              :text="$t('button.edit') + $t('space') + $t('playlist.title')" 
              />
            <ButtonOrListItem 
              :simple="simple"
              small 
              icon="delete" 
              color="secondary" 
              @click="deleting = true" 
              :text="$t('button.delete') + $t('space') + $t('playlist.title')" 
              />
          </template>
        </ButtonOrListGroup>
      </ManagePlaylist>
    </div>
    <mu-button class="float-right" small flat @click="canAccess ? onClickToggle() : ''" :disabled="!canAccess">
      {{$t('playlist.childrenCount', { num: data.children.length, s: data.children.length >= 2 ? 's' : '' })}}<mu-icon :value="toggle ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"/>
    </mu-button>
    <div class="display-flex">
      <mu-avatar :color="simple ? 'primary' : 'transparent'" :text-color="simple ? '' : themeTextColor" class="inline-icon" :class="!edit ? ( canAccess ? 'clickable' : '') : ''" @click="!edit ? ( canAccess ? onClickToggle() : '') : ''">
        <mu-icon 
          :value="toggle ? 'remove_circle' : 'add_circle'" 
          v-if="!edit"
          /> 
        <mu-icon 
          v-if="!simple && edit"
          value="open_with" 
          class="drag-handle"
          /> 
        <mu-icon 
          v-else
          value="playlist_play"
          /> 
      </mu-avatar>
      <div class="playlist-info-wrapper">
        <span :class="[simple ? '' : 'item-title', canAccess ? 'clickable' : '']" @click="canAccess ? onClickToggle() : ''">
          {{playlist.name}}
        </span>
        <span v-if="edit && !simple">
          <small v-if="playlist.access_mode === 0">
            <mu-icon value="lock_open" class="inline-icon"></mu-icon>{{$t('playlist.accessModeOpen')}}
          </small>
          <small v-else-if="playlist.access_mode === 1">
            <mu-icon value="lock" class="inline-icon"></mu-icon>{{$t('playlist.accessModeClose')}}
          </small>
          <small v-else-if="playlist.access_mode === 2">
            <mu-icon value="schedule" class="inline-icon"></mu-icon>{{$t('playlist.accessModeSince')}}{{stringTimeFormatter(playlist.can_access_by)}}
          </small>
        </span>
        <div class="description-wrapper wordwrap"><small>{{playlist.description}}</small></div>
      </div>
    </div>
    <div class="clearfix" />
    <transition name="fade">
      <div :class="simple ? 'media-simple-container' : 'media-container'" v-if="toggle">
        <div v-if="data.children.length === 0">
          <small>{{$t('playlist.emptyMedia')}}</small>
        </div>
        <draggable :list="obj.children" :options="{ 'group': 'channel', 'disabled': !edit, 'handle': '.drag-handle' }" @change="onSort" :class="edit && !simple ? 'drag-area' : ''">
          <component 
            :is="child.type === 'media' ? 'MediaItem' : 'PlaylistItem'" 
            :obj="child" 
            :editable="editable"
            :enrollments="enrollments"
            :simple="simple"
            :channelId="channelId"
            :mediaId="mediaId"
            :defaultToggle="recursiveSearch(child, mediaId)"
            :forceToggle="forceToggle"
            v-for="(child, index) in obj.children" 
            @update="onUpdate($event, index)"
            @deleted="onDeleted(index)"
            />
        </draggable>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from "axios";
import draggable from 'vuedraggable'

import MediaItem from '@/components/Media/MediaItem.vue'
import ManageMedia from '@/components/Media/ManageMedia'

import ManagePlaylist from '@/components/Playlist/ManagePlaylist'

import ButtonOrListItem from '@/components/Common/ButtonOrListItem'
import ButtonOrListGroup from '@/components/Common/ButtonOrListGroup'

import theme from '@/mixins/theme.js'

export default {
  name: "PlaylistItem",
  mixins: [theme],
  components: {
    draggable,
    MediaItem,
    ManagePlaylist,
    ManageMedia,
    ButtonOrListItem,
    ButtonOrListGroup,
  },
  props: {
    obj: Object,
    editable: Boolean,
    enrollments: {
      type: Array,
      default() {
        return [];
      }
    },
    simple: {
      type: Boolean,
      default: false
    },
    channelId: {
      type: String,
      default: ""
    },
    mediaId: {
      type: String,
      default: ""
    },
    defaultToggle: {
      type: Boolean,
      default: false
    },
    forceToggle: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      data: JSON.parse(JSON.stringify(this.obj)),
      editing: false,
      deleting: false,
      
      addingPlaylist: false,
      addingMedia: false,
      
      toggle: this.defaultToggle || this.forceToggle,
    }
  },
  watch: {
    obj: {
      handler(newValue) {
        this.data = JSON.parse(JSON.stringify(this.obj))
      },
      deep: true
    },
    forceToggle(newValue) {
      if(this.canAccess) {
        this.toggle = newValue;
      }
    },
  },
  computed: {
    isPreview() {
      if(this.$store.getters.previewMode) {
        return true;
      } else {
        return false;
      }
    },
    playlist() {
      if(this.data.obj) {
        return this.data.obj;
      } else {
        return {
          name: '',
          access_mode: 0
        };
      }
    },
    edit() {
      return !this.isPreview && this.editable;
    },
    canAccess() {
      if(this.edit) {
        return true;
      } else {
        if(this.playlist.access_mode === 0) {
          return true;
        } else if(this.playlist.access_mode === 1) {
          return false;
        } else if(this.playlist.access_mode === 2) {
          var now = Date.now();
          var d = new Date(this.playlist.can_access_by);
          return now >= d.getTime();
        } else {
          return true;
        }
      }
    }
  },
  methods: {
    // Event on this layer
    onCancel() {
      this.editing = false;
      this.deleting = false;
      this.addingPlaylist = false;
      this.addingMedia = false;
    },
    onSave(payload, toast = true) {
      this.editing = false;
      this.deleting = false;
      axios.put(this.$store.getters.urlApi + "playlist/" + this.data.id, payload, {
        headers: {
          token: this.$store.state.auth.token,
          'content-type': 'application/json'
        },
      }).then(() => {
        if(toast) {
          this.$toast.success(this.$t('toast.edit'));
        }
        this.$emit('updated');
      });
    },
    onDelete() {
      this.editing = false;
      this.deleting = false;
      axios.delete(this.$store.getters.urlApi + "playlist/" + this.data.id, {
        headers: {
          token: this.$store.state.auth.token
        }
      }).then(() => {
        this.$toast.success(this.$t('toast.delete'));
        this.$emit('deleted');
      });
    },
    onAddPlaylist(payload) {
      this.addingPlaylist = false;
      axios.post(this.$store.getters.urlApi + "playlist/channel/" + this.channelId, payload, {
        headers: {
          token: this.$store.state.auth.token,
          'content-type': 'application/json'
        },
      }).then(val => {
        this.data.children.push({
          id: val.data.data.playlist._id,
          type: 'playlist',
          children: [],
          obj: val.data.data.playlist
        });
        this.$emit('update', this.data);
        this.$toast.success(this.$t('toast.create'));
        this.toggle = true;
      });
    },
    onAddMedia(payload) {
      this.addingPlaylist = false;
      this.addingMedia = false;
      
      var formData = payload.file;
      var data = payload.data;
      var upload = payload.upload;
      axios.post(this.$store.getters.urlApi + "media/channel/" + this.channelId, data, {
        headers: {
          token: this.$store.state.auth.token
        }
      }).then(val => {
        if(upload) {
          formData.append('_method', "PUT");
          this.$store.commit('uploadQueueAppend', {
            uploadId: Math.random().toString(36).substring(7),
            formData: formData,
            url: "media/" + val.data.data.media._id,
            text: data['name']
          });
        }
        this.data.children.push({
          id: val.data.data.media._id,
          type: 'media',
          obj: val.data.data.media
        });
        this.$toast.success(this.$t('toast.create'));
        this.$emit('update', this.data);
        this.toggle = true;
      });
    },
    onClickToggle() {
      this.toggle = !this.toggle;
    },
    
    // Event from children
    onSort(data) {
      this.$nextTick(() => {
        this.$emit('sort');
      });
    },
    onUpdate(payload, index) {
      this.data.children[index] = payload;
      this.$emit('update', this.data);
    },
    onDeleted(index) {
      if(this.data.children[index].hasOwnProperty('children') && Array.isArray(this.data.children[index].children)) {
        var children = this.data.children[index].children;
        children.forEach((child, i) => {
          this.data.children.push(child);
        });
      }
      this.data.children.splice(index, 1);
      this.$emit('update', this.data);
    },
    
    // String
    stringTimeFormatter(str) {
      var d = new Date(str);
      return d.toLocaleString();
    },
    
    recursiveSearch(object, id) {
      if(object.id === id) {
        return true;
      } else {
        if(object.hasOwnProperty('children') && object.children.length > 0) {
          var result = false;
          object.children.forEach(child => {
            result = result || this.recursiveSearch(child, id);
          });
          return result;
        } else {
          return false;
        }
      }
      
    },
  }
};
</script>

<style scoped>
.playlist-wrapper {
  padding-left: 4px;
  padding-right: 4px;
  padding-bottom: 2px;
  padding-top: 2px;
}
.media-container {
  margin-left: 40px;
  padding: 2px;
}
.media-simple-container {
  margin-left: 20px;
}
.description-wrapper {
  margin-left: 4px;
}
.drag-area {
  border-style: dashed;
  border-width: 1px;
  min-height: 50px;
}
.inline-icon {
  min-width: 40px;
  max-width: 40px;
}
.button-wrapper {
  margin-left: 40px;
  margin-right: 2px;
  margin-bottom: 4px;
  margin-top: 4px;
}
.divider-padding {
  margin-top: 4px;
  margin-bottom: 4px;
}
.playlist-info-wrapper {
  padding-left: 4px;
}
.clickable {
  cursor: pointer;
}
.clickable:hover {
  opacity: 0.5;
}
</style>